import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['content']
  static values = { url: String }

  connect() {
    if (this.urlValue) {
      this.fetchSnapshots()
    }
  }

  fetchSnapshots() {
    this.contentTarget.innerHTML = `
      <div class="flex items-center justify-center py-8 text-base-content/50">
        <span class="loading loading-spinner loading-sm mr-2"></span> Loading snapshots...
      </div>
    `

    fetch(this.urlValue, {
      headers: { 'Accept': 'application/json' }
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.json()
      })
      .then(data => this.renderSnapshots(data))
      .catch(error => this.renderError(error.message))
  }

  renderSnapshots(snapshots) {
    if (!snapshots || snapshots.length === 0) {
      this.contentTarget.innerHTML = `
        <div class="text-center py-8 text-base-content/50">
          <i class="fas fa-camera text-2xl mb-2"></i>
          <p>No snapshots yet.</p>
          <p class="text-sm">Take a snapshot to save the current state of your instance.</p>
        </div>
      `
      return
    }

    const rows = snapshots.map(snapshot => {
      const createdAt = snapshot.created_at ? this.timeAgo(new Date(snapshot.created_at)) : 'Unknown'
      const size = snapshot.size_gigabytes ? `${snapshot.size_gigabytes.toFixed(1)} GB` : 'N/A'

      return `
        <tr>
          <td class="font-mono text-sm">${this.escapeHtml(snapshot.name)}</td>
          <td class="text-sm">${size}</td>
          <td class="text-sm text-base-content/60">${createdAt}</td>
          <td class="text-right">
            <div class="flex justify-end gap-1">
              <button class="btn btn-ghost btn-xs text-warning"
                      data-action="click->snapshots#confirmRestore"
                      data-snapshot-id="${snapshot.id}">
                Restore
              </button>
              <button class="btn btn-ghost btn-xs text-error"
                      data-action="click->snapshots#confirmDelete"
                      data-snapshot-id="${snapshot.id}"
                      data-snapshot-name="${this.escapeHtml(snapshot.name)}">
                Delete
              </button>
            </div>
          </td>
        </tr>
      `
    }).join('')

    this.contentTarget.innerHTML = `
      <div class="overflow-x-auto">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>Name</th>
              <th>Size</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `
  }

  confirmRestore(event) {
    const snapshotId = event.currentTarget.dataset.snapshotId
    if (!confirm('This will rebuild the droplet from this snapshot. All current data will be replaced. Are you sure?')) {
      return
    }

    // Find the instance path from the current URL and submit a form
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = this.buildActionUrl('restore_snapshot_image')

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrfToken
      form.appendChild(tokenInput)
    }

    const idInput = document.createElement('input')
    idInput.type = 'hidden'
    idInput.name = 'snapshot_id'
    idInput.value = snapshotId
    form.appendChild(idInput)

    document.body.appendChild(form)
    form.submit()
  }

  confirmDelete(event) {
    const snapshotId = event.currentTarget.dataset.snapshotId
    const snapshotName = event.currentTarget.dataset.snapshotName
    if (!confirm(`Delete snapshot "${snapshotName}"? This cannot be undone.`)) {
      return
    }

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = this.buildActionUrl('delete_snapshot')

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrfToken
      form.appendChild(tokenInput)
    }

    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'delete'
    form.appendChild(methodInput)

    const idInput = document.createElement('input')
    idInput.type = 'hidden'
    idInput.name = 'snapshot_id'
    idInput.value = snapshotId
    form.appendChild(idInput)

    document.body.appendChild(form)
    form.submit()
  }

  buildActionUrl(action) {
    // The list_snapshots URL looks like /instances/:id/list_snapshots
    // Replace the last segment to build other action URLs
    return this.urlValue.replace(/list_snapshots$/, action)
  }

  renderError(message) {
    this.contentTarget.innerHTML = `
      <div class="text-center py-8 text-base-content/50">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        Unable to load snapshots: ${message}
      </div>
    `
  }

  timeAgo(date) {
    const seconds = Math.floor((new Date() - date) / 1000)
    if (seconds < 60) return 'just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days}d ago`
    return date.toLocaleDateString()
  }

  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}
